// Shared client model for the Hotel-Edition module pages (Instagram-Fotos, LinkedIn-Beiträge,
// Veröffentlichungsplan). Edit/delete go through the DFY routes (Scheibe 1): PATCH/DELETE
// /api/dfy/posts/:id with the version the customer saw.
import { useMutation } from "@tanstack/react-query"
import { apiRequest, queryClient } from "@/lib/queryClient"
import { useToast } from "@/hooks/use-toast"
import type { PillarPost } from "@shared/schema"

export type EditReason = "plan_review" | "queued" | "in_progress" | "handed_over" | "closed" | "reel_manual"
export type Editability = { canEdit: boolean; canDelete: boolean; reason: EditReason }
export type HotelPost = PillarPost & { editability?: Editability | null }

export const EDIT_HINT = "Bearbeiten und Löschen auch nach der Freigabe möglich, bis der Beitrag eingeplant ist."

/** Reels (B2, PLAN §7.4): never handed to the planner, the team publishes them. */
export const REEL_EDIT_HINT = "Reels veröffentlicht Ihr Team. Bearbeiten und Löschen sind auch nach der Freigabe möglich, bis die Woche abgeschlossen ist."

export const LOCK_HINT: Partial<Record<EditReason, string>> = {
  in_progress: "Wird gerade eingeplant. Änderungen sind im Moment nicht möglich.",
  handed_over: "Bereits eingeplant. Änderungen nehmen Sie bitte im Planer vor oder sprechen Ihr Team an.",
  closed: "Diese Woche ist abgeschlossen.",
}

/** Every list that shows hotel posts refreshes after an edit/delete. */
function refreshHotelLists() {
  queryClient.invalidateQueries({ queryKey: ["/api/pillar-posts"] })
  queryClient.invalidateQueries({ queryKey: ["/api/generated-posts", "publishing-plan"] })
  queryClient.invalidateQueries({ queryKey: ["/api/dfy/weekly-plan"] })
  queryClient.invalidateQueries({ queryKey: ["/api/dashboard/overview"] })
}

function errorText(err: unknown, fallback: string): string {
  const e = err as { status?: number; data?: { error?: string } } | undefined
  if (e?.status === 409) return "Der Beitrag wurde inzwischen geändert oder eingeplant. Bitte laden Sie die Seite neu."
  if (e?.status === 404) return "Den Beitrag gibt es nicht mehr. Bitte laden Sie die Seite neu."
  return e?.data?.error || fallback
}

export function useHotelPostMutations(onDone?: () => void) {
  const { toast } = useToast()
  const edit = useMutation({
    mutationFn: ({ id, title, postContent, version }: { id: string; title: string; postContent: string; version: number }) =>
      apiRequest("PATCH", `/api/dfy/posts/${id}`, { title, postContent, version }),
    onSuccess: () => {
      refreshHotelLists()
      toast({ title: "Beitrag gespeichert" })
      onDone?.()
    },
    onError: (err) => toast({ title: "Speichern fehlgeschlagen", description: errorText(err, "Bitte versuchen Sie es erneut."), variant: "destructive" }),
  })
  const remove = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      apiRequest("DELETE", `/api/dfy/posts/${id}?version=${encodeURIComponent(String(version))}`),
    onSuccess: () => {
      refreshHotelLists()
      toast({ title: "Beitrag gelöscht" })
      onDone?.()
    },
    onError: (err) => toast({ title: "Löschen fehlgeschlagen", description: errorText(err, "Bitte versuchen Sie es erneut."), variant: "destructive" }),
  })
  return { edit, remove }
}
